import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Scale, FileCheck, PackageCheck } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, AnnotatedDocument, FaqList, faqSchema, ClosingCta, usePrimaryCta } from '../components/common/B2B';

// Carta de porte nacional (nov-2026), contenido diseñado desde cero (Jesús: «sin
// verte limitado por lo que había»): qué gana quien la usa (contrato + control +
// prueba de entrega), el cambio frente al papel, el documento real anotado con lo
// que exige la ley en cada casilla, el recorrido de la oficina al destino,
// integración y preguntas frecuentes. Afirmaciones legales: las que ya publicaba
// la web; en la obligatoriedad, la formulación precisa (art. 10 / 10 bis).
// Imágenes: renderizador real con datos ficticios
// (marketing/documentos-muestra/render-carta-porte-clasica.mjs).

const PAGE_URL = { es: 'https://www.bluecmr.com/documentos/carta-porte', en: 'https://www.bluecmr.com/en/documents/road-waybill' };

// Marcas sobre carta-porte-clasica-completa.webp (en % de la imagen; posiciones
// medidas en el HTML del renderizador: QR, NIF, fechas, peso, precio, firmas y pie).
// En inglés, el documento pintado en inglés (carta-porte-clasica-completa-en.webp) y las
// marcas trasladadas caja a caja desde el render español (4-nov-2026).
const MARKS = {
  es: [
    { x: 87.7, y: 5.6 }, { x: 27.9, y: 13.3 }, { x: 36.5, y: 37.0 }, { x: 79.1, y: 63.0 },
    { x: 71.5, y: 71.1 }, { x: 29.7, y: 83.9 }, { x: 90.1, y: 94.0 },
  ],
  en: [
    { x: 87.7, y: 5.5 }, { x: 27.9, y: 13.2 }, { x: 36.5, y: 36.6 }, { x: 79.1, y: 63.4 },
    { x: 71.5, y: 71.4 }, { x: 29.7, y: 84.1 }, { x: 90.1, y: 94.1 },
  ],
};
const DOC_IMG = {
  es: { hero: '/images/documentos/carta-porte-clasica.webp', full: '/images/documentos/carta-porte-clasica-completa.webp', h: 1360 },
  en: { hero: '/images/documentos/carta-porte-clasica-en.webp', full: '/images/documentos/carta-porte-clasica-completa-en.webp', h: 1375 },
};

const COPY = {
  es: {
    seoTitle: 'Carta de porte digital: requisitos de la Ley 15/2009 y DeCA | BlueCMR',
    seoDescription: 'La carta de porte nacional en formato electrónico: contrato, documento de control y prueba de entrega en un solo documento, según la Ley 15/2009.',
    seoKeywords: 'carta de porte, carta de porte digital, carta de porte electrónica, ley 15/2009, artículo 10 bis, carta de porte obligatoria, carta de porte y deca',
    heroEyebrow: 'Carta de porte · Transporte nacional',
    heroTitle: 'Contrato, documento de control y prueba de entrega, en una sola carta de porte',
    heroText: 'Emítala desde su sistema o desde BlueCMR, fírmela en el móvil y tenga el acuse de entrega firmado en cuanto se descarga la mercancía.',
    heroChecks: ['Ley 15/2009 y artículo 10 bis', 'Incluye los datos del DeCA', 'Firma electrónica avanzada'],
    heroAlt: 'Carta de porte nacional de BlueCMR en diseño clásico',
    platform: 'Conozca la plataforma',
    whyEyebrow: 'Por qué',
    whyTitle: 'Un documento que hace el trabajo de tres',
    why: [
      { icon: Scale, title: 'Contrato de transporte', text: 'Cargador y porteador firman las condiciones pactadas con firma electrónica avanzada, como prevé la Ley 15/2009.' },
      { icon: FileCheck, title: 'Documento de control', text: 'Lleva los datos de la Orden FOM/2861/2012, así que sirve como DeCA: no hay que emitir otro documento.' },
      { icon: PackageCheck, title: 'Prueba de entrega', text: 'El destinatario firma la recepción en destino y todas las partes reciben la copia firmada al momento.' },
    ],
    changeEyebrow: 'Del papel a lo digital',
    changeTitle: 'Lo que cambia en el día a día',
    changeHead: ['Con papel', 'Con BlueCMR'],
    change: [
      ['Tres ejemplares impresos que hay que firmar a mano', 'Un único documento electrónico que comparten todas las partes'],
      ['La entrega se confirma cuando vuelve el papel', 'El acuse firmado llega en el momento de la descarga'],
      ['Un documento de control aparte', 'La carta de porte ya incluye los datos del DeCA'],
      ['Reservas e incidencias anotadas a mano', 'Registradas en ruta, con hora, fotografía y firma'],
      ['Cajas de archivo durante al menos un año', 'Conservación con todas las evidencias y búsqueda inmediata'],
    ],
    docEyebrow: 'El documento',
    docTitle: 'Todo lo que exige la ley, en su sitio',
    docText: 'Así es una carta de porte emitida con BlueCMR (datos de ejemplo). Cada mención obligatoria tiene su casilla.',
    docAlt: 'Carta de porte nacional completa emitida con BlueCMR, con datos de ejemplo',
    notes: [
      { title: 'Código QR', text: 'La inspección abre y verifica el documento al instante.' },
      { title: 'Partes con NIF y domicilio', text: 'Cargador, porteador y destinatario, como piden los artículos 10 y 10 bis.' },
      { title: 'Lugares, fechas y horas', text: 'De carga y de entrega prevista; con porteador efectivo, también la hora (art. 10 bis).' },
      { title: 'Mercancía y masa', text: 'Naturaleza, bultos, embalaje y peso bruto y neto.' },
      { title: 'Precio y quién lo paga', text: 'Precio del transporte y gastos, a cargo del remitente o del destinatario (art. 10).' },
      { title: 'Firmas de las partes', text: 'Remitente y porteador firman el contrato (art. 11); el destinatario, la recepción (art. 12).' },
      { title: 'Emitida en soporte electrónico', text: 'Con acuerdo de las partes, como permite el artículo 15, y con su dirección de verificación.' },
    ],
    howEyebrow: 'Cómo funciona',
    howTitle: 'De la oficina al destino',
    steps: [
      { place: 'Oficina', title: 'Emisión', text: 'Desde su TMS o ERP, desde un fichero o con una plantilla en BlueCMR.' },
      { place: 'Muelle de carga', title: 'Firma del contrato', text: 'Cargador y porteador firman desde el móvil, sin instalar nada.' },
      { place: 'En ruta', title: 'Seguimiento', text: 'El conductor registra reservas, incidencias y cambios de vehículo, y lleva la carta de porte a mano para la inspección.' },
      { place: 'Destino', title: 'Entrega', text: 'El destinatario firma la recepción con firma biométrica u OTP.' },
      { place: 'Después', title: 'Archivo', text: 'Todas las partes tienen la copia firmada y el documento se conserva con sus evidencias.' },
    ],
    intEyebrow: 'Integración',
    intTitle: 'Sin teclear dos veces',
    intText: 'Las cartas de porte pueden salir solas de su sistema de gestión, con los datos obligatorios validados antes de emitir.',
    intList: ['API REST y webhooks para TMS y ERP', 'Importación de ficheros de cualquier tipo, también PDF', 'Plantillas y datos guardados en la plataforma', 'Envío y firma de varios documentos a la vez'],
    intLink: 'Integraciones',
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Dudas habituales',
    faq: [
      { q: '¿Es obligatoria la carta de porte?', a: 'El artículo 10 de la Ley 15/2009 permite a cualquiera de las partes, cargador o porteador, exigir que se emita. En los contratos con porteador efectivo y precio superior a 150 €, el artículo 10 bis obliga a formalizarla por escrito. No hacerlo cuando es exigible puede constituir infracción según la LOTT (Ley 16/1987).' },
      { q: '¿Quién la firma?', a: 'El cargador y el porteador (art. 11 de la Ley 15/2009). El destinatario no firma la carta de porte como tal: firma la recepción de la mercancía como acuse de recibo (art. 12).' },
      { q: '¿Sirve como DeCA?', a: 'Sí. Una carta de porte digital que incluya todos los datos del documento de control de la Orden FOM/2861/2012 cumple también esa obligación, así que no hace falta llevar los dos documentos. La de BlueCMR los incluye siempre.' },
      { q: '¿Tiene validez la carta de porte electrónica?', a: 'Sí. El artículo 15 de la Ley 15/2009 la admite expresamente, siempre que consista en un registro electrónico de datos legibles. La firma electrónica avanzada del Reglamento eIDAS da al documento autenticidad e integridad.' },
      { q: '¿Cuánto tiempo hay que conservarla?', a: 'Al menos un año, según el artículo 10 bis.6 de la Ley 15/2009. BlueCMR la conserva con todas sus evidencias y acceso permanente para las partes.' },
      { q: '¿Y si el transporte es internacional?', a: 'Entonces el documento es el CMR, que se rige por el Convenio CMR de 1956; en formato electrónico, el eCMR. BlueCMR emite los dos con la misma operativa.' },
    ],
    relatedTitle: 'Otros documentos',
    related: [
      { to: '/documentos/ecmr', title: 'eCMR', text: 'La carta de porte internacional, en formato electrónico.' },
      { to: '/documentos/documento-control', title: 'DeCA', text: 'El documento de control digital del transporte nacional.' },
      { to: '/documentos/transporte-adr', title: 'Documento de transporte ADR', text: 'Para mercancías peligrosas, nacional e internacional.' },
    ],
    closing: 'Digitalice la carta de porte de sus transportes nacionales',
    closingText: 'Contrato, documento de control y prueba de entrega, firmados y conservados.',
    breadcrumb: ['Inicio', 'Documentos', 'Carta de porte'],
  },
  en: {
    seoTitle: 'Digital consignment note for Spain (Law 15/2009) | BlueCMR',
    seoDescription: 'The Spanish domestic consignment note (carta de porte) in electronic form: contract, control document and proof of delivery in a single document.',
    seoKeywords: 'carta de porte, spanish consignment note, digital waybill spain, law 15/2009, control document',
    heroEyebrow: 'Consignment note · Domestic transport in Spain',
    heroTitle: 'Contract, control document and proof of delivery, in a single consignment note',
    heroText: 'Issue it from your system or from BlueCMR, sign it on the phone and get the signed delivery receipt as soon as the goods are unloaded.',
    heroChecks: ['Law 15/2009 and article 10 bis', 'Includes the control document data', 'Advanced electronic signature'],
    heroAlt: 'BlueCMR Spanish consignment note in classic layout',
    platform: 'Explore the platform',
    whyEyebrow: 'Why',
    whyTitle: 'One document doing the work of three',
    why: [
      { icon: Scale, title: 'Transport contract', text: 'Shipper and carrier sign the agreed terms with an advanced electronic signature, as provided by Law 15/2009.' },
      { icon: FileCheck, title: 'Control document', text: 'It carries the data of Order FOM/2861/2012, so it works as the DeCA: no other document needs to be issued.' },
      { icon: PackageCheck, title: 'Proof of delivery', text: 'The consignee signs the receipt at destination and every party gets the signed copy straight away.' },
    ],
    changeEyebrow: 'From paper to digital',
    changeTitle: 'What changes day to day',
    changeHead: ['On paper', 'With BlueCMR'],
    change: [
      ['Three printed copies to be signed by hand', 'A single electronic document shared by all parties'],
      ['Delivery is confirmed when the paper comes back', 'The signed receipt arrives the moment the goods are unloaded'],
      ['A separate control document', 'The consignment note already includes the control document data'],
      ['Reservations and incidents written by hand', 'Recorded on the road, with time, photograph and signature'],
      ['Archive boxes kept for at least a year', 'Preservation with all the evidence and instant search'],
    ],
    docEyebrow: 'The document',
    docTitle: 'Everything the law requires, in its place',
    docText: 'This is a consignment note issued with BlueCMR (sample data). Each mandatory particular has its box.',
    docAlt: 'Full Spanish consignment note issued with BlueCMR, with sample data',
    notes: [
      { title: 'QR code', text: 'Inspectors open and verify the document instantly.' },
      { title: 'Parties with tax ID and address', text: 'Shipper, carrier and consignee, as required by articles 10 and 10 bis.' },
      { title: 'Places, dates and times', text: 'Of loading and planned delivery; with an actual carrier, the time too (art. 10 bis).' },
      { title: 'Goods and mass', text: 'Nature, packages, packaging and gross and net weight.' },
      { title: 'Price and who pays it', text: 'Price of carriage and charges, paid by the shipper or the consignee (art. 10).' },
      { title: 'Signatures of the parties', text: 'Shipper and carrier sign the contract (art. 11); the consignee, the receipt (art. 12).' },
      { title: 'Issued electronically', text: 'With the parties’ agreement, as allowed by article 15, and with its verification address.' },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From the office to the destination',
    steps: [
      { place: 'Office', title: 'Issue', text: 'From your TMS or ERP, from a file or with a template in BlueCMR.' },
      { place: 'Loading dock', title: 'Contract signature', text: 'Shipper and carrier sign on the phone, with nothing to install.' },
      { place: 'On the road', title: 'Tracking', text: 'The driver records reservations, incidents and vehicle changes, and has the consignment note at hand for inspections.' },
      { place: 'Destination', title: 'Delivery', text: 'The consignee signs the receipt with a biometric signature or OTP.' },
      { place: 'Afterwards', title: 'Archive', text: 'Every party has the signed copy and the document is preserved with its evidence.' },
    ],
    intEyebrow: 'Integration',
    intTitle: 'No typing twice',
    intText: 'Consignment notes can be generated automatically from your management system, with the mandatory data validated before issue.',
    intList: ['REST API and webhooks for TMS and ERP', 'Import of files of any type, PDF included', 'Templates and saved data in the platform', 'Sending and signing several documents at once'],
    intLink: 'Integrations',
    faqEyebrow: 'FAQ',
    faqTitle: 'Common questions',
    faq: [
      { q: 'Is the consignment note mandatory?', a: 'Article 10 of Law 15/2009 allows either party, shipper or carrier, to require one to be issued. In contracts with an actual carrier and a price above €150, article 10 bis requires it to be in writing. Failing to do so when required may be an infringement under the Spanish road transport act (Law 16/1987).' },
      { q: 'Who signs it?', a: 'The shipper and the carrier (art. 11 of Law 15/2009). The consignee does not sign the consignment note as such: it signs the receipt of the goods as acknowledgement (art. 12).' },
      { q: 'Does it work as the DeCA?', a: 'Yes. A digital consignment note that includes all the control document data of Order FOM/2861/2012 also fulfils that obligation, so there is no need to carry both documents. BlueCMR’s always includes them.' },
      { q: 'Is the electronic consignment note valid?', a: 'Yes. Article 15 of Law 15/2009 expressly allows it, as long as it is an electronic record of legible data. The eIDAS advanced electronic signature gives the document authenticity and integrity.' },
      { q: 'How long must it be kept?', a: 'At least one year, under article 10 bis.6 of Law 15/2009. BlueCMR preserves it with all its evidence and permanent access for the parties.' },
      { q: 'What if the transport is international?', a: 'Then the document is the CMR, governed by the 1956 CMR Convention; in electronic form, the eCMR. BlueCMR issues both with the same workflow.' },
    ],
    relatedTitle: 'Other documents',
    related: [
      { to: '/en/documents/ecmr', title: 'eCMR', text: 'The international consignment note, in electronic form.' },
    ],
    closing: 'Digitalise the consignment note of your domestic transport',
    closingText: 'Contract, control document and proof of delivery, signed and preserved.',
    breadcrumb: ['Home', 'Documents', 'Consignment note'],
  },
};

const CartaPorteDocumentoPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);
  const notes = c.notes.map((n, i) => ({ ...n, ...MARKS[lang][i] }));

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        type="article"
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: 'https://www.bluecmr.com/' },
          { name: c.breadcrumb[1], url: 'https://www.bluecmr.com/documentos' },
          { name: c.breadcrumb[2], url: PAGE_URL[lang] }
        ]}
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: c.heroTitle, description: c.seoDescription, url: PAGE_URL[lang] },
          faqSchema(c.faq)
        ]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          documentImage={{ src: DOC_IMG[lang].hero, alt: c.heroAlt }}
          primary={primaryCta}
          secondary={{ to: getLocalizedUrl('/plataforma'), label: c.platform }}
          compact
        >
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {c.heroChecks.map((item) => (
              <li key={item} className="flex items-center gap-2 text-slate-200">
                <Check className="w-4 h-4 text-blue-300" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </PageHero>

        {/* Un documento que hace el trabajo de tres */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.whyEyebrow} title={c.whyTitle} />
            <div className="grid md:grid-cols-3 gap-x-10 gap-y-10">
              {c.why.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="border-t-2 border-slate-900 pt-6">
                    <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />
                    <h3 className="text-xl font-semibold text-slate-900">{item.title}</h3>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Del papel a lo digital */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.changeEyebrow} title={c.changeTitle} />
            <div className="border-b border-slate-300">
              <div className="hidden md:grid grid-cols-2 gap-x-10 pb-3 text-xs font-semibold uppercase tracking-[0.14em]">
                <span className="text-slate-500">{c.changeHead[0]}</span>
                <span className="text-primary-700">{c.changeHead[1]}</span>
              </div>
              {c.change.map(([before, after]) => (
                <div key={before} className="grid md:grid-cols-2 gap-x-10 gap-y-1 border-t border-slate-300 py-5">
                  <p className="text-slate-500 leading-relaxed"><span className="md:hidden text-xs font-semibold uppercase tracking-wider text-slate-400 mr-2">{c.changeHead[0]}</span>{before}</p>
                  <p className="flex items-start gap-2.5 font-medium text-slate-900 leading-relaxed">
                    <ArrowRight className="mt-1 w-4 h-4 flex-shrink-0 text-primary-700" />
                    <span>{after}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* El documento real, anotado */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.docEyebrow} title={c.docTitle} text={c.docText} />
            <AnnotatedDocument
              src={DOC_IMG[lang].full}
              alt={c.docAlt}
              width="1100"
              height={DOC_IMG[lang].h}
              notes={notes}
            />
          </div>
        </section>

        {/* De la oficina al destino */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.howEyebrow} title={c.howTitle} />
            <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-10">
              {c.steps.map((step, i) => (
                <li key={step.title} className="border-t-2 border-slate-900 pt-5">
                  <p className="flex items-baseline gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
                    <span className="tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    <span>{step.place}</span>
                  </p>
                  <h3 className="mt-3 font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Integración */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.intEyebrow} title={c.intTitle} text={c.intText} className="" />
              <Link to={getLocalizedUrl('/interoperabilidad')} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:gap-3 transition-all">
                {c.intLink} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <ul className="lg:col-span-7 border-b border-slate-200">
              {c.intList.map((item) => (
                <li key={item} className="flex items-center gap-3 border-t border-slate-200 py-4 text-slate-800">
                  <Check className="w-5 h-5 flex-shrink-0 text-primary-700" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Preguntas frecuentes */}
        <section className="py-20 md:py-24 bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionIntro eyebrow={c.faqEyebrow} title={c.faqTitle} className="" />
            </div>
            <div className="lg:col-span-8">
              <FaqList items={c.faq} />
            </div>
          </div>
        </section>

        {/* Otros documentos */}
        <section className="py-14 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">{c.relatedTitle}</h2>
            <ul className="grid md:grid-cols-3 gap-x-8">
              {c.related.map((r) => (
                <li key={r.to} className="border-t border-slate-200">
                  <Link to={r.to} className="group flex items-start justify-between gap-4 py-5">
                    <span>
                      <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{r.title}</span>
                      <span className="mt-1 block text-sm text-slate-600">{r.text}</span>
                    </span>
                    <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default CartaPorteDocumentoPage;
